import React from 'react';

import { Card, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { uniqBy } from 'ramda';
import { IconColors } from 'src/components/icons/icon-utils';
import { InfoIcon } from 'src/components/icons/InfoIcon';
import { TemplateIcon } from 'src/components/icons/TemplateIcon';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { alineLinkButtonStylesWithUnderline } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  card: {
    '&:not(:last-child)': {
      marginRight: theme.spacing(2),
    },
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    width: 256,
  },
  container: {
    display: 'flex',
  },
  icon: {
    marginBottom: theme.spacing(2),
  },
  licenseText: {
    ...alineLinkButtonStylesWithUnderline,
    fontSize: theme.typography.h2.fontSize,
    fontWeight: theme.typography.h2.fontWeight,
  },
  metricText: {
    ...theme.appTypography.fieldLabel,
    color: theme.palette.text.secondary,
  },
  showMoreText: {
    fontSize: 12,
  },
}));

interface CommunityWorkflowMetricsProps extends React.HTMLAttributes<HTMLElement> {
  workflowId: UUID;
}

export function CommunityWorkflowMetrics({
  className,
  workflowId,
}: CommunityWorkflowMetricsProps): JSX.Element {
  const { classes } = useStyles();
  const { data, loading } = Apollo.useGetCommunityWorkflowMetricsQuery({
    variables: { workflowId },
  });
  const licenses =
    data?.workflow?.files.flatMap(file => {
      return file.document.license ? [file.document.license] : [];
    }) ?? [];
  const uniqLicenses = uniqBy(license => license.id, licenses);
  const [firstLicense = null] = uniqLicenses;
  const showOverflowText = uniqLicenses.length > 1;

  if (loading || !data?.workflow)
    return (
      <div className={clsx(classes.container, className)}>
        {Array.from({ length: 3 }).map((_, index) => {
          return (
            <Card key={index} className={classes.card}>
              <Skeleton className={classes.icon} height={40} variant="rectangular" width={40} />
              <Typography className={classes.metricText}>
                <Skeleton variant="text" width={96} />
              </Typography>
              <Typography variant="h2">
                <Skeleton variant="text" width={128} />
              </Typography>
            </Card>
          );
        })}
      </div>
    );

  return (
    <div className={clsx(classes.container, className)}>
      <Card className={classes.card}>
        <InfoIcon className={classes.icon} fill={IconColors.DarkGrey} />
        <Typography className={classes.metricText}># of Times Imported</Typography>
        <Typography variant="h2">{data.workflow.numTimesImported}</Typography>
      </Card>
      <Card className={classes.card}>
        <TemplateIcon className={classes.icon} />
        <Typography className={classes.metricText}>Total Templates</Typography>
        <Typography variant="h2">{data.workflow.fileCount}</Typography>
      </Card>
      <Card className={classes.card}>
        <InfoIcon className={classes.icon} fill={IconColors.DarkGrey} />
        <Typography className={classes.metricText}>License</Typography>
        {firstLicense && (
          <Typography
            className={classes.licenseText}
            onClick={() => {
              openLink(firstLicense.url);
            }}
          >
            {firstLicense.name}
          </Typography>
        )}
        {showOverflowText && (
          <Typography className={classes.showMoreText} color="textSecondary">
            {`+${uniqLicenses.length - 1} more`}
          </Typography>
        )}
      </Card>
    </div>
  );
}
